import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHero } from "@/components/page-hero";
import { ArrowRight, Check } from "lucide-react";
import { getService, services, type Service } from "@/content/services";
import { CTASection } from "@/components/cta-section";
import { SeoFAQ } from "@/components/seo-faq";
import { AccreditationsStrip } from "@/components/accreditations-strip";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ReviewStamp } from "@/components/review-stamp";
import { RelatedContent } from "@/components/related-content";
import { getRelatedContent } from "@/lib/related";
import { IndustryLinkPlanSection } from "@/components/industry-link-plan";
import { serviceLinkPlan } from "@/lib/internal-links";
import { fetchServiceLinkPlanSpec, resolveServicePlan } from "@/lib/link-plans";
import { RelatedToolsModule } from "@/components/related-tools-module";
import { EabBannerBlock } from "@/components/eab-banner";
import {
  buildHead,
  generatePageTitle,
  serviceSchema,
  breadcrumbSchema,
  ensureFAQs,
  webPageSchema,
} from "@/lib/seo";

const pkFor = (s: Service) => s.primaryKeyword ?? s.slug.replace(/-/g, " ");

const SERVICE_H1: Record<string, { h1: string; lede: string }> = {
  "hr-outsourcing": {
    h1: "HR Outsourcing Services for UK Employers",
    lede: "Trusted HR outsourcing for employers, businesses and SMEs across the UK.",
  },
  "employment-law-advice": {
    h1: "Employment Law Advice for UK Employers",
    lede: "On-demand employment law advice for every UK employer, business and HR team.",
  },
  "hr-for-startups": {
    h1: "HR for Startups, Expert Support for UK Founders",
    lede: "Specialist HR for startup employers and founder-led businesses, from first hire to Series A.",
  },
  "hr-for-smes": {
    h1: "HR for SMEs, Practical Support for UK Employers",
    lede: "Hands-on HR for SME employers and growing businesses past 25, 50 and 100 employees.",
  },
  "tupe-advice": {
    h1: "TUPE Advice for Employers, UK Specialists",
    lede: "Solicitor-led TUPE advice for UK employers buying, selling, outsourcing or insourcing.",
  },
  "redundancy-support": {
    h1: "Redundancy Support for Employers, UK Specialists",
    lede: "Defensible individual and collective redundancy support for UK employers and HR teams.",
  },
  "employment-tribunal-support": {
    h1: "Employment Tribunal Defence for UK Employers",
    lede: "Specialist tribunal defence for UK employers and businesses, 98% successful outcome rate.",
  },
  "hr-health-check": {
    h1: "HR Health Check for UK Employers",
    lede: "Independent HR health check for every employer, business and HR function in the UK.",
  },
};

export const Route = createFileRoute("/services/$slug")({
  loader: ({ params }) => {
    const service = getService(params.slug);
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { service } = loaderData;
    const pk = pkFor(service);
    const path = `/services/${service.slug}`;
    const title = service.seoTitle ?? generatePageTitle(pk, "service");
    const faqs = ensureFAQs(pk, service.faqs, "service");
    const crumbs = [
      { label: "Home", path: "/" },
      { label: "Services", path: "/services" },
      { label: service.shortName, path },
    ];
    return buildHead({
      title,
      description: service.metaDescription,
      path,
      keywords: service.keywords,
      schemas: [
        serviceSchema({ name: service.name, description: service.metaDescription, serviceType: service.name }),
        webPageSchema({
          path,
          name: title,
          description: service.metaDescription,
          faqs,
          hasBreadcrumb: true,
          lastReviewed: service.lastReviewed,
        }),
        // FAQ JSON-LD is nested inside the WebPage node above.
        breadcrumbSchema(crumbs, path),
      ],
    });
  },
  component: ServicePage,
  notFoundComponent: () => (
    <div className="px-6 py-24 text-center">
      <h1 className="font-serif text-4xl text-brand">Service not found</h1>
      <Link to="/services" className="mt-6 inline-block text-accent">
        Back to services
      </Link>
    </div>
  ),
});

function ServicePage() {
  const { service } = Route.useLoaderData() as { service: Service };
  const related = services.filter((s) => s.slug !== service.slug).slice(0, 3);
  const pk = pkFor(service);
  // Built-in plan renders server-side for SEO; a CMS override (if any) swaps in on the client.
  const { data: override } = useQuery({
    queryKey: ["service-link-plan", service.slug],
    queryFn: () => fetchServiceLinkPlanSpec(service.slug),
    staleTime: 60_000,
  });
  const linkPlan = override ? resolveServicePlan(service.slug, override) : serviceLinkPlan(service.slug);

  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Services", path: "/services" },
          { label: service.shortName, path: `/services/${service.slug}` },
        ]}
        eyebrow="Services"
        title={service.h1 ?? SERVICE_H1[service.slug]?.h1 ?? service.heroHeadline}
        subtitle={
          SERVICE_H1[service.slug]?.lede ?? `${service.primaryKeyword} for UK employers and businesses.`
        }
        intro={service.heroSubheadline}
        lastReviewed={service.lastReviewed}
        author={service.author}
        actions={
          <>
            <Link
              to="/contact-hr-experts"
              className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-3 text-sm font-medium text-brand-foreground hover:brightness-110"
            >
              Get a quote
              <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/hr-packages"
              className="inline-flex items-center rounded-md border border-border bg-card px-5 py-3 text-sm font-medium text-foreground hover:bg-secondary"
            >
              View pricing
            </Link>
          </>
        }
        aside={
          <div className="relative rounded-md border border-border bg-card p-8 shadow-sm">
            <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1 rounded-l-md bg-accent" />
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">What's included</p>
            <ul className="mt-4 space-y-3">
              {service.includes.map((i) => (
                <li key={i} className="flex gap-3 text-sm text-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-accent" />
                  {i}
                </li>
              ))}
            </ul>
            <p className="mt-6 border-t border-border pt-4 text-sm text-muted-foreground">{service.pricingNote}</p>
          </div>
        }
      />


      <section className="px-6 pt-20 pb-8 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <p className="text-lg leading-relaxed text-foreground/90">{service.description}</p>
        </div>
      </section>

      {service.sections?.length ? (
        <section className="px-6 pb-8 lg:px-8">
          <div className="mx-auto max-w-5xl space-y-16">

            {service.sections.map((sec) => (
              <article key={sec.heading}>
                <h2 className="font-serif text-2xl leading-tight text-brand sm:text-3xl">{sec.heading}</h2>
                <div className="mt-5 space-y-4">
                  {sec.paragraphs.map((para) => (
                    <p key={para.slice(0, 40)} className="leading-relaxed text-muted-foreground">
                      {para}
                    </p>
                  ))}
                </div>
                {sec.bullets?.length ? (
                  <ul className="mt-6 space-y-3 border-l-2 border-accent/40 pl-6">
                    {sec.bullets.map((b) => (
                      <li key={b} className="text-sm leading-relaxed text-foreground/90">
                        {b}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="bg-card/40 px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-serif text-3xl text-brand">Why UK employers choose our {pk}</h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            What businesses tell us makes the difference when they switch their {pk} to EmployerHQ.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">

            {service.benefits.map((b) => (
              <div key={b.title} className="rounded-2xl bg-card p-8 ring-1 ring-black/5">
                <h3 className="font-serif text-lg text-brand">{b.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-serif text-3xl text-brand">How our {pk} works for your business</h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            A straightforward process designed for busy employers, from first call to ongoing partnership.
          </p>
          <ol className="mt-12 space-y-6">
            {service.process.map((step, i) => (
              <li key={step.title} className="flex gap-6 rounded-2xl border border-border bg-card p-6">
                <span className="font-serif text-3xl text-accent">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-serif text-lg text-brand">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <AccreditationsStrip />
      <SeoFAQ primaryKeyword={pk} faqs={service.faqs} type="service" />

      <EabBannerBlock path={`/services/${service.slug}`} source="service_page" />

      <RelatedToolsModule plan={linkPlan} subject={service.shortName} source="service_page" />

      <IndustryLinkPlanSection
        plan={linkPlan}
        sector={service.shortName}
        variant="service"
        hideGroups={["calculators", "templates"]}
      />

      <RelatedContent
        heading={`More on ${pk}`}
        items={getRelatedContent({
          type: "service",
          slug: service.slug,
          primaryKeyword: pk,
          topics: [service.shortName],
          diversify: true,
        })}
      />

      <CTASection headline={service.ctaHeadline} />

      <section className="px-6 pb-24 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-serif text-2xl text-brand">Related services</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">

            {related.map((r) => (
              <Link
                key={r.slug}
                to="/services/$slug"
                params={{ slug: r.slug }}
                className="group rounded-2xl bg-card p-6 ring-1 ring-black/5 hover:ring-accent/30"
              >
                <h3 className="font-serif text-lg text-brand">{r.shortName}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{r.summary}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent">
                  Read about {r.shortName} <ArrowRight className="size-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
